import { useEffect, useMemo, useState } from 'react';
import { Card, Text, Button, Input, Spinner } from '@fluentui/react-components';
import { useAuth } from '@/auth/AuthContext';
import {
  getPricingConfig,
  updatePricingConfig,
  PricingTierConfig,
} from '@/services/pricingService';
import './Pricing.css';

function defaultTiers(): PricingTierConfig[] {
  return [
    { min: 1, max: 50, rate: 5, label: '1-50', sortOrder: 1 },
    { min: 51, max: 150, rate: 4, label: '51-150', sortOrder: 2 },
    { min: 151, max: 300, rate: 3, label: '151-300', sortOrder: 3 },
    { min: 301, max: 500, rate: 2, label: '301-500', sortOrder: 4 },
    { min: 501, max: null, rate: 1, label: '501+', sortOrder: 5 },
  ];
}

function normalizeLabel(min: number, max: number | null): string {
  return max === null ? `${min}+` : `${min}-${max}`;
}

export default function PricingPage() {
  const { token, user } = useAuth();
  const [tiers, setTiers] = useState<PricingTierConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const canEdit = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const data = await getPricingConfig(token);
        setTiers(data.length > 0 ? data : defaultTiers());
      } catch (err: any) {
        console.error('Failed to load pricing config:', err);
        setError(err?.message || 'Failed to load pricing config');
        setTiers(defaultTiers());
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  const validationError = useMemo(() => {
    if (!tiers.length) return 'At least one tier is required';

    const sorted = [...tiers].sort((a, b) => a.min - b.min);
    let expectedMin = 1;

    for (let i = 0; i < sorted.length; i += 1) {
      const t = sorted[i];
      if (!Number.isInteger(t.min) || t.min < 1) return 'Min users must be an integer >= 1';
      if (t.max !== null && (!Number.isInteger(t.max) || t.max < t.min)) return 'Max users must be null or >= min';
      if (typeof t.rate !== 'number' || Number.isNaN(t.rate) || t.rate < 0) return 'Rate must be a non-negative number';
      if (t.min !== expectedMin) return `Tier ranges must be contiguous (expected min ${expectedMin})`;
      if (t.max === null && i !== sorted.length - 1) return 'Only the final tier can have no max';
      if (t.max !== null) expectedMin = t.max + 1;
      if (t.max === null) return '';
    }

    return 'Final tier must have no max';
  }, [tiers]);

  const updateTier = (index: number, patch: Partial<PricingTierConfig>) => {
    setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  };

  const handleReset = () => {
    setTiers(defaultTiers());
    setError('');
    setSaved(false);
  };

  const handleSave = async () => {
    if (!token || !canEdit) return;
    if (validationError) {
      setError(validationError);
      return;
    }

    const sorted = [...tiers]
      .sort((a, b) => a.min - b.min)
      .map((t, idx) => ({
        ...t,
        label: normalizeLabel(t.min, t.max),
        sortOrder: idx + 1,
      }));

    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const updated = await updatePricingConfig(sorted, token);
      setTiers(updated.length > 0 ? updated : sorted);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err: any) {
      console.error('Failed to save pricing config:', err);
      setError(err?.message || 'Failed to save pricing config');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pricing-loading">
        <Spinner size="large" />
        <Text>Loading pricing configuration...</Text>
      </div>
    );
  }

  return (
    <div className="pricing-container">
      <div className="page-header">
        <div>
          <h1>Pricing</h1>
          <Text size={300}>Manage graduated per-user pricing shown in the customer admin portal</Text>
        </div>
      </div>

      {!canEdit && (
        <Card className="pricing-banner read-only">
          <Text>You have read-only access to pricing.</Text>
        </Card>
      )}

      {error && (
        <Card className="pricing-banner error">
          <Text>{error}</Text>
        </Card>
      )}

      {saved && (
        <Card className="pricing-banner success">
          <Text>Pricing updated successfully.</Text>
        </Card>
      )}

      <Card className="pricing-card">
        <div className="pricing-table-header">
          <Text weight="semibold">Tier</Text>
          <Text weight="semibold">Min Users</Text>
          <Text weight="semibold">Max Users</Text>
          <Text weight="semibold">Rate / User</Text>
        </div>

        {tiers
          .sort((a, b) => a.min - b.min)
          .map((tier, idx) => (
            <div className="pricing-row" key={`${tier.min}-${tier.max ?? 'plus'}`}>
              <Text className="tier-label">{normalizeLabel(tier.min, tier.max)}</Text>
              <Input
                type="number"
                value={String(tier.min)}
                onChange={(_, d) => updateTier(idx, { min: Number(d.value || 0) })}
                disabled={!canEdit || saving}
              />
              <Input
                type="number"
                value={tier.max === null ? '' : String(tier.max)}
                onChange={(_, d) => {
                  const value = d.value.trim();
                  updateTier(idx, { max: value === '' ? null : Number(value) });
                }}
                placeholder="blank = no max"
                disabled={!canEdit || saving}
              />
              <Input
                type="number"
                value={String(tier.rate)}
                onChange={(_, d) => updateTier(idx, { rate: Number(d.value || 0) })}
                step="0.01"
                disabled={!canEdit || saving}
              />
            </div>
          ))}

        <Text size={200} className="pricing-hint">
          Rules: tiers must start at 1, ranges must be contiguous, and only the final tier can be open-ended.
        </Text>
      </Card>

      {canEdit && (
        <div className="pricing-actions">
          <Button appearance="primary" onClick={handleSave} disabled={saving || !!validationError}>
            {saving ? <Spinner size="tiny" /> : 'Save Pricing'}
          </Button>
          <Button appearance="secondary" onClick={handleReset} disabled={saving}>
            Reset to Default
          </Button>
        </div>
      )}
    </div>
  );
}
